import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Header from '../components/layout/Header';
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiEdit2,
  FiSave,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
  FiUserPlus,
  FiBell
} from 'react-icons/fi';

interface PersonalInfo {
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  studentId: string;
  email: string;
  phoneNumber: string;
  alternatePhone?: string;
  dateOfBirth: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  emergencyContacts: EmergencyContact[];
  demographics: {
    ethnicity?: string;
    gender?: string;
    citizenship?: string;
  };
  preferences: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    mailingList: boolean;
  };
}

interface EmergencyContact {
  id: string;
  name: string;
  relationship: string;
  phoneNumber: string;
  email?: string;
}

const MyPersonalInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<PersonalInfo>>({});

  useEffect(() => {
    const fetchPersonalInfo = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        const docRef = doc(db, 'personalInfo', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as PersonalInfo;
          setPersonalInfo(data);
          setFormData(data);
        } else {
          setError('Personal information not found. Please contact support.');
        }
      } catch (err) {
        console.error('Error fetching personal info:', err);
        setError('Failed to load personal information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalInfo();
  }, [currentUser]);

  const handleEdit = (section: string) => {
    setEditingSection(section);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setEditingSection(null);
    setFormData(personalInfo || {});
  };

  const handleSave = async (section: string) => {
    if (!currentUser) return;

    try {
      const docRef = doc(db, 'personalInfo', currentUser.uid);
      await updateDoc(docRef, formData);

      setPersonalInfo(formData as PersonalInfo);
      setEditingSection(null);
      setSuccessMessage('Changes saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating personal info:', err);
      setError('Failed to save changes. Please try again.');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...(prev.address || personalInfo?.address || {} as any),
        [field]: value
      }
    }));
  };

  const handlePreferenceChange = (field: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...(prev.preferences || personalInfo?.preferences || {} as any),
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-uc-navy">Loading personal information...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!personalInfo) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600">Failed to load personal information</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">MY PERSONAL INFO</h1>
          <p className="text-gray-600 mt-2">Manage your personal and contact information</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiCheckCircle className="text-green-600 mt-0.5" size={20} />
            <div>
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5" size={20} />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiUser className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Basic Information</h2>
            </div>
            {editingSection !== 'basic' ? (
              <button
                onClick={() => handleEdit('basic')}
                className="flex items-center gap-2 text-uc-navy hover:text-uc-gold transition-colors"
              >
                <FiEdit2 size={18} />
                <span className="text-sm font-medium">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('basic')}
                  className="flex items-center gap-2 bg-uc-gold text-white px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  <FiSave size={16} />
                  <span className="text-sm font-medium">Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <FiX size={16} />
                  <span className="text-sm font-medium">Cancel</span>
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                {editingSection === 'basic' ? (
                  <input
                    type="text"
                    value={formData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                {editingSection === 'basic' ? (
                  <input
                    type="text"
                    value={formData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
                {editingSection === 'basic' ? (
                  <input
                    type="text"
                    value={formData.middleName || ''}
                    onChange={(e) => handleInputChange('middleName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.middleName || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Name</label>
                {editingSection === 'basic' ? (
                  <input
                    type="text"
                    value={formData.preferredName || ''}
                    onChange={(e) => handleInputChange('preferredName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.preferredName || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Student ID</label>
                <p className="text-gray-900">{personalInfo.studentId}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                <p className="text-gray-900">{personalInfo.dateOfBirth}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiPhone className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Contact Information</h2>
            </div>
            {editingSection !== 'contact' ? (
              <button
                onClick={() => handleEdit('contact')}
                className="flex items-center gap-2 text-uc-navy hover:text-uc-gold transition-colors"
              >
                <FiEdit2 size={18} />
                <span className="text-sm font-medium">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('contact')}
                  className="flex items-center gap-2 bg-uc-gold text-white px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  <FiSave size={16} />
                  <span className="text-sm font-medium">Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <FiX size={16} />
                  <span className="text-sm font-medium">Cancel</span>
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiMail className="inline mr-2" size={16} />
                  Email Address
                </label>
                {editingSection === 'contact' ? (
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiPhone className="inline mr-2" size={16} />
                  Phone Number
                </label>
                {editingSection === 'contact' ? (
                  <input
                    type="tel"
                    value={formData.phoneNumber || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.phoneNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Phone</label>
                {editingSection === 'contact' ? (
                  <input
                    type="tel"
                    value={formData.alternatePhone || ''}
                    onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.alternatePhone || 'N/A'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiMapPin className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Mailing Address</h2>
            </div>
            {editingSection !== 'address' ? (
              <button
                onClick={() => handleEdit('address')}
                className="flex items-center gap-2 text-uc-navy hover:text-uc-gold transition-colors"
              >
                <FiEdit2 size={18} />
                <span className="text-sm font-medium">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('address')}
                  className="flex items-center gap-2 bg-uc-gold text-white px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  <FiSave size={16} />
                  <span className="text-sm font-medium">Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <FiX size={16} />
                  <span className="text-sm font-medium">Cancel</span>
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                {editingSection === 'address' ? (
                  <input
                    type="text"
                    value={formData.address?.street || ''}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.address.street}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  {editingSection === 'address' ? (
                    <input
                      type="text"
                      value={formData.address?.city || ''}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                    />
                  ) : (
                    <p className="text-gray-900">{personalInfo.address.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  {editingSection === 'address' ? (
                    <input
                      type="text"
                      value={formData.address?.state || ''}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                    />
                  ) : (
                    <p className="text-gray-900">{personalInfo.address.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
                  {editingSection === 'address' ? (
                    <input
                      type="text"
                      value={formData.address?.zipCode || ''}
                      onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                    />
                  ) : (
                    <p className="text-gray-900">{personalInfo.address.zipCode}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                {editingSection === 'address' ? (
                  <input
                    type="text"
                    value={formData.address?.country || ''}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
                  />
                ) : (
                  <p className="text-gray-900">{personalInfo.address.country}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Contacts */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiUserPlus className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Emergency Contacts</h2>
            </div>
          </div>

          <div className="p-6">
            {personalInfo.emergencyContacts.map((contact, index) => (
              <div key={contact.id} className={`pb-6 ${index < personalInfo.emergencyContacts.length - 1 ? 'border-b border-gray-200 mb-6' : ''}`}>
                <h3 className="font-medium text-uc-navy mb-4">Contact {index + 1}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <p className="text-gray-900">{contact.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <p className="text-gray-900">{contact.relationship}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <p className="text-gray-900">{contact.phoneNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <p className="text-gray-900">{contact.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Communication Preferences */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FiBell className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Communication Preferences</h2>
            </div>
            {editingSection !== 'preferences' ? (
              <button
                onClick={() => handleEdit('preferences')}
                className="flex items-center gap-2 text-uc-navy hover:text-uc-gold transition-colors"
              >
                <FiEdit2 size={18} />
                <span className="text-sm font-medium">Edit</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('preferences')}
                  className="flex items-center gap-2 bg-uc-gold text-white px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors"
                >
                  <FiSave size={16} />
                  <span className="text-sm font-medium">Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <FiX size={16} />
                  <span className="text-sm font-medium">Cancel</span>
                </button>
              </div>
            )}
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive important updates via email</p>
              </div>
              {editingSection === 'preferences' ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.preferences?.emailNotifications || false}
                    onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-uc-gold/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uc-gold"></div>
                </label>
              ) : (
                <span className={`px-3 py-1 rounded-full text-sm ${
                  personalInfo.preferences.emailNotifications
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {personalInfo.preferences.emailNotifications ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">SMS Notifications</p>
                <p className="text-sm text-gray-600">Receive alerts via text message</p>
              </div>
              {editingSection === 'preferences' ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.preferences?.smsNotifications || false}
                    onChange={(e) => handlePreferenceChange('smsNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-uc-gold/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uc-gold"></div>
                </label>
              ) : (
                <span className={`px-3 py-1 rounded-full text-sm ${
                  personalInfo.preferences.smsNotifications
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {personalInfo.preferences.smsNotifications ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Mailing List</p>
                <p className="text-sm text-gray-600">Receive university newsletters and updates</p>
              </div>
              {editingSection === 'preferences' ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.preferences?.mailingList || false}
                    onChange={(e) => handlePreferenceChange('mailingList', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-uc-gold/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uc-gold"></div>
                </label>
              ) : (
                <span className={`px-3 py-1 rounded-full text-sm ${
                  personalInfo.preferences.mailingList
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {personalInfo.preferences.mailingList ? 'Subscribed' : 'Unsubscribed'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FiLock className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Security</h2>
            </div>
          </div>

          <div className="p-6">
            <button className="w-full md:w-auto flex items-center gap-2 bg-uc-navy text-white px-6 py-3 rounded-lg hover:bg-uc-navy-light transition-colors">
              <FiLock size={18} />
              <span className="font-medium">Change Password</span>
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Update your password to keep your account secure
            </p>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            <FiAlertCircle size={20} />
            Important Information
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Keep your contact information up to date to receive important notifications</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Changes to legal name require submission of official documentation to the Registrar's Office</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Emergency contacts should be people we can reach in case of an urgent situation</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default MyPersonalInfoPage;
